package fr.frux.feature.auth

import androidx.compose.runtime.Composable
import fr.frux.core.common.error.AppError
import fr.frux.core.designsystem.error.userMessage
import fr.frux.feature.auth.resources.Res
import fr.frux.feature.auth.resources.auth_error_app_outdated
import fr.frux.feature.auth.resources.auth_error_code_invalid_format
import fr.frux.feature.auth.resources.auth_error_code_invalid_or_expired
import fr.frux.feature.auth.resources.auth_error_code_required
import fr.frux.feature.auth.resources.auth_error_email_invalid
import fr.frux.feature.auth.resources.auth_error_email_required
import fr.frux.feature.auth.resources.auth_error_email_too_long
import fr.frux.feature.auth.resources.auth_error_username_invalid
import fr.frux.feature.auth.resources.auth_error_username_required
import fr.frux.feature.auth.resources.auth_error_username_reserved
import fr.frux.feature.auth.resources.auth_error_username_taken
import fr.frux.feature.auth.resources.auth_error_username_too_long
import fr.frux.feature.auth.resources.auth_error_username_too_short
import fr.frux.shared.identity.IdentityPolicy
import fr.frux.shared.identity.IdentityProblem
import org.jetbrains.compose.resources.stringResource

/**
 * Traduction en français, en composition (#176) : le domaine partagé (`IdentityPolicy`, `AppError`)
 * est aussi compilé par le serveur, dont les messages ne sont pas destinés à un écran. Le
 * ViewModel ne porte que le type [AuthError] ; le libellé n'existe qu'ici.
 */
@Composable
fun AuthError.userMessage(): String =
    when (this) {
        is AuthError.Identity -> {
            problem.frenchMessage()
        }

        AuthError.CodeRequired -> {
            stringResource(Res.string.auth_error_code_required)
        }

        AuthError.CodeInvalidFormat -> {
            stringResource(Res.string.auth_error_code_invalid_format, IdentityPolicy.OTP_CODE_LENGTH)
        }

        AuthError.CodeInvalidOrExpired -> {
            stringResource(Res.string.auth_error_code_invalid_or_expired)
        }

        AuthError.UsernameTaken -> {
            stringResource(Res.string.auth_error_username_taken)
        }

        is AuthError.Remote -> {
            // Un 404 ici ne peut pas venir d'une saisie : les routes d'auth existent ou pas.
            // C'est un contrat client-serveur perime (vu avec la bascule /api/v1, #280) — le
            // libelle generique « Cet element n'existe plus » etait un non-sens sur cet ecran.
            if (error is AppError.NotFound) {
                stringResource(Res.string.auth_error_app_outdated)
            } else {
                error.userMessage()
            }
        }
    }

@Composable
private fun IdentityProblem.frenchMessage(): String =
    when (this) {
        IdentityProblem.EMAIL_REQUIRED -> {
            stringResource(Res.string.auth_error_email_required)
        }

        IdentityProblem.EMAIL_INVALID -> {
            stringResource(Res.string.auth_error_email_invalid)
        }

        IdentityProblem.EMAIL_TOO_LONG -> {
            stringResource(Res.string.auth_error_email_too_long)
        }

        IdentityProblem.USERNAME_REQUIRED -> {
            stringResource(Res.string.auth_error_username_required)
        }

        IdentityProblem.USERNAME_TOO_SHORT -> {
            stringResource(Res.string.auth_error_username_too_short, IdentityPolicy.MIN_USERNAME_LENGTH)
        }

        IdentityProblem.USERNAME_TOO_LONG -> {
            stringResource(Res.string.auth_error_username_too_long, IdentityPolicy.MAX_USERNAME_LENGTH)
        }

        IdentityProblem.USERNAME_INVALID -> {
            stringResource(Res.string.auth_error_username_invalid)
        }

        IdentityProblem.USERNAME_RESERVED -> {
            stringResource(Res.string.auth_error_username_reserved)
        }
    }
